import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { useAuthStore } from '../../../stores';

export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const setMustChangePassword = useAuthStore((s) => s.setMustChangePassword);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (password: string) => authService.setPassword(password),
    onSuccess: () => {
      setMustChangePassword(false);
      toast.success('Password updated successfully');
      navigate('/dashboard');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    mutation.mutate(newPassword);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4">
      <div className="w-full max-w-md">
        <div className="rounded-panel bg-surface border border-line p-8 shadow-m">
          <h1 className="mb-2 text-2xl font-bold text-ink font-display">
            Change Your Password
          </h1>
          <p className="mb-6 text-sm text-ink-2">
            For security, you must set a new password before continuing.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                New password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                placeholder="Minimum 6 characters"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Confirm password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                placeholder="Re-enter your new password"
                required
              />
            </div>

            {error && (
              <p className="text-sm text-danger">{error}</p>
            )}

            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full rounded-pill bg-brand px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-ink disabled:opacity-50"
            >
              {mutation.isPending ? 'Setting password...' : 'Set a new password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
